import { useCallback, useEffect, useMemo, useState } from 'react';

import type { ParsedQs } from 'qs';
import qs from 'qs';
import type { NavigateOptions } from 'react-router';
import { useLocation } from 'react-router';

import { useNavigate } from '@change-corgi/core-react/router';

export function useQueryString(): ParsedQs {
	const { search } = useLocation();

	return useMemo(
		() => ({
			...qs.parse(search, { ignoreQueryPrefix: true }),
		}),
		[search],
	);
}

/**
 * Use this to partially update the current url's query params.
 *
 * @example
 * const updateQueryParams = useUpdateQueryParams();
 * // ...
 * updateQueryParams({ foo: 42 }); // updates/adds foo and conserve other params
 * updateQueryParams({ foo: undefined }); // clears foo and conserve other params
 *
 */
export function useUpdateQueryString(): (query: ParsedQs, options?: Pick<NavigateOptions, 'replace'>) => void {
	const location = useLocation();
	const navigate = useNavigate();

	return useCallback(
		(newQuery, options) => {
			const query = qs.parse(location.search, { ignoreQueryPrefix: true });
			navigate(
				`${location.pathname}${qs.stringify(
					{ ...query, ...newQuery },
					{ addQueryPrefix: true, arrayFormat: 'repeat' },
				)}`,
				{
					...options,
					preventScrollReset: true,
					forceMode: 'internal',
				},
			);
		},
		[location, navigate],
	);
}

export function useQueryParamValue(key: string): string | undefined {
	const queryString = useQueryString();
	// oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
	const value = queryString[key] as string | string[] | undefined;
	// oxlint-disable-next-line no-nested-ternary
	return value === undefined ? undefined : Array.isArray(value) ? value[0] : value;
}

type MapToOptionalString<T extends readonly string[]> = {
	[K in keyof T]: string | undefined;
};

/**
 * Passing a n-tuple of keys (using "as const") is recommended
 *
 * @example
 * const [foo, bar] = useMultipleQueryParamsValue(['foo', 'bar'] as const);
 * console.log({foo, bar});
 */
export function useMultipleQueryParamsValue<S extends readonly string[]>(keys: S): MapToOptionalString<S> {
	const queryString = useQueryString();
	const dep = keys.join('|'); // trick to avoid recreating the array on every render when the passed array is inline
	return useMemo(() => {
		return keys.map((key) => {
			// oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
			const value = queryString[key] as string | string[] | undefined;
			// oxlint-disable-next-line no-nested-ternary
			return value === undefined ? undefined : Array.isArray(value) ? value[0] : value;
		}) as MapToOptionalString<S>;
		// oxlint-disable-next-line react-hooks/exhaustive-deps
	}, [dep, queryString]);
}

/** @knip-ignore */
export function useQueryParamValues(key: string): readonly string[] {
	const queryString = useQueryString();
	// oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
	const value = queryString[key] as string | string[] | undefined;
	// oxlint-disable-next-line no-nested-ternary
	const values = value === undefined ? [] : Array.isArray(value) ? [...value] : [value];
	// eslint-disable-next-line react-hooks/use-memo
	return useMemo(() => values, values); // oxlint-disable-line react-hooks/exhaustive-deps
}

/**
 * Returns a query param value and clears it from the url by navigating to the same url but without the query param
 *
 * The key cannot change over time
 */
export function useClearedQueryParamValue(key: string): string | undefined {
	const value = useQueryParamValue(key);
	const [initialValue] = useState(value);
	const updateQueryString = useUpdateQueryString();

	useEffect(() => {
		if (value === undefined) return;

		updateQueryString({ [key]: undefined }, { replace: true });
	}, [key, value, updateQueryString]);

	return initialValue;
}

/**
 * Returns a list of query param value and clears them from the url by navigating to the same url but without the query params
 *
 * The keys cannot change over time
 *
 * Passing a n-tuple of keys (using "as const") is recommended
 *
 * @example
 * const [foo, bar] = useClearedMultipleQueryParamsValues(['foo', 'bar'] as const);
 * console.log({foo, bar});
 */
export function useClearedMultipleQueryParamsValues<S extends readonly string[]>(keys: S): MapToOptionalString<S> {
	const values = useMultipleQueryParamsValue(keys);
	const [initialValues] = useState(values);
	const updateQueryString = useUpdateQueryString();

	const dep = keys.join('|'); // trick to avoid recreating the array on every render when the passed array is inline
	useEffect(() => {
		const keysToClear = values
			.map((value, index) => (value !== undefined ? keys[index] : undefined))
			.filter((value) => value !== undefined);

		if (keysToClear.length === 0) return;

		updateQueryString(
			keysToClear.reduce<ParsedQs>((acc, key) => {
				// oxlint-disable-next-line no-param-reassign
				acc[key] = undefined;
				return acc;
			}, {}),
			{ replace: true },
		);
		// oxlint-disable-next-line react-hooks/exhaustive-deps
	}, [dep, updateQueryString]);

	return initialValues;
}
